// A17 — Job Categories. Same TVP2 §9 styling as the other admin list
// pages: bordered Search filter card → bordered action bar + table card.
// Bootstrap palette (Search blue · Add green · Reset/Delete grey).
//
// Add / Edit happens in a Dialog (the form is small enough that an
// inline edit page is overkill). Delete soft-disables via PATCH
// is_active=false (the existing API hides DELETE behind that toggle —
// inactive categories aren't surfaced in the customer mobile picker but
// historical jobs keep their FK).

import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface JobCategoryRow {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  price_tier_id: string | null;
}

interface PriceTierRow {
  id: string;
  code: string;
  label: string;
  base_price_cents: number;
  is_active: boolean;
}

interface FormState {
  id?: string;
  name: string;
  description: string;
  price_tier_id: string;
}

function formatHKD(cents: number): string {
  return `HK$${(cents / 100).toLocaleString("en-HK")}`;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "category_name_taken":
      return "A category with that name already exists.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

export function JobCategoriesList() {
  const { show, ToastEl } = useToast();

  const [nameQ, setNameQ] = useState("");
  const [statusQ, setStatusQ] = useState<"" | "active" | "inactive">("");
  const [filters, setFilters] = useState<{ q?: string; is_active?: string }>(
    {},
  );

  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);

  const list = usePagedQuery<JobCategoryRow>("job-categories", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  // P1 — fetch all active price tiers for the assignment dropdown. Tiers are
  // few (3-5 in production), so paging isn't worth it; ask for limit=100.
  const tiers = useQuery<{ items: PriceTierRow[] }>({
    queryKey: ["admin", "price-tiers"],
    queryFn: () => apiFetch<{ items: PriceTierRow[] }>("/v1/admin/price-tiers?limit=100"),
    staleTime: 60_000,
  });
  const activeTiers = (tiers.data?.items ?? []).filter((t) => t.is_active);
  const tierLookup = new Map(activeTiers.map((t) => [t.id, t] as const));

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setFilters({
      q: nameQ.trim() || undefined,
      is_active:
        statusQ === "active"
          ? "true"
          : statusQ === "inactive"
            ? "false"
            : undefined,
    });
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };
  const onReset = () => {
    setNameQ("");
    setStatusQ("");
    setFilters({});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const body = {
        name: editing.name,
        description: editing.description || undefined,
      };
      let categoryId = editing.id;
      if (categoryId) {
        await apiFetch(`/v1/admin/job-categories/${categoryId}`, {
          method: "PATCH",
          body,
        });
      } else {
        const created = await apiFetch<{ ok: true; id: string }>(
          "/v1/admin/job-categories",
          { method: "POST", body },
        );
        categoryId = created.id;
      }
      // P1 — assign the chosen price tier via the dedicated endpoint
      // (gated by can_manage_pricing, separate from the category PATCH gate).
      // Skipped if the user picked the unchanged value on edit.
      const targetTierId = editing.price_tier_id || null;
      const originalTierId = list.data?.items.find((c) => c.id === categoryId)?.price_tier_id ?? null;
      if (targetTierId && targetTierId !== originalTierId) {
        await apiFetch(`/v1/admin/job-categories/${categoryId}/price-tier`, {
          method: "POST",
          body: { price_tier_id: targetTierId },
        });
      }
    },
    onSuccess: () => {
      show("Saved", "success");
      setEditing(null);
      list.invalidate();
    },
    onError: (err) => show(friendlyError(err, "Save failed"), "error"),
  });

  // Bulk soft-delete via PATCH is_active=false. Mirrors the AdminUserList
  // semantics: idempotent per-row, summarised result toast.
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results: Array<{ id: string; ok: boolean }> = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]!;
        try {
          await apiFetch(`/v1/admin/job-categories/${id}`, {
            method: "PATCH",
            body: { is_active: false },
          });
          results.push({ id, ok: true });
        } catch {
          results.push({ id, ok: false });
        }
        if (i < ids.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
      return results;
    },
    onSuccess: (results) => {
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      show(
        failCount === 0
          ? `Disabled ${okCount} of ${results.length}`
          : `Disabled ${okCount} of ${results.length}; ${failCount} failed`,
        failCount === 0 ? "success" : "warning",
      );
      setSelected([]);
      setDeleteOpen(false);
      list.invalidate();
    },
  });

  const columns: DataTableColumn<JobCategoryRow>[] = [
    { field: "name", headerName: "Name" },
    {
      field: "description",
      headerName: "Description",
      render: (r) => r.description ?? "-",
    },
    {
      field: "price_tier_id",
      headerName: "Price tier",
      render: (r) => {
        if (!r.price_tier_id) return "-";
        const t = tierLookup.get(r.price_tier_id);
        return t ? `${t.label} (${formatHKD(t.base_price_cents)})` : "Unknown";
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      render: (r) => (r.is_active ? "Active" : "Inactive"),
    },
  ];

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const searchBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
  };
  const addBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const primaryBtnDialog = {
    ...baseBtn,
    px: 3,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search filter card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
        }}
      >
        <Tabs
          value="search"
          sx={{
            minHeight: 38,
            borderBottom: `1px solid ${tokens.colors.admin.border}`,
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.colors.admin.tabActive,
              height: 2,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              textTransform: "none",
              fontSize: 13,
              color: tokens.colors.admin.textHeading,
              "& .MuiTab-iconWrapper": {
                marginRight: 0.5,
                marginBottom: "0 !important",
                "& svg": { fontSize: 16 },
              },
            },
          }}
        >
          <Tab
            value="search"
            iconPosition="start"
            icon={<SearchIcon />}
            label="Search"
          />
        </Tabs>
        <Box component="form" onSubmit={onSearch} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Name"
              variant="standard"
              fullWidth
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
            />
            <TextField
              label="Status"
              variant="standard"
              select
              fullWidth
              value={statusQ}
              onChange={(e) => setStatusQ(e.target.value as typeof statusQ)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" sx={searchBtn}>
              Search
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={onReset}
              sx={greyBtn}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Action bar + table card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={addBtn}
            onClick={() =>
              setEditing({ name: "", description: "", price_tier_id: "" })
            }
          >
            Add
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            disabled={selected.length === 0 || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>

        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.id}
            checkboxSelection
            selectedIds={selected}
            onSelectionChange={setSelected}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() =>
                    setEditing({
                      id: row.id,
                      name: row.name,
                      description: row.description ?? "",
                      price_tier_id: row.price_tier_id ?? "",
                    })
                  }
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Disable"
                  onClick={() => {
                    setSelected([row.id]);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          />
        </Box>
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing?.id ? "Edit Job Category" : "Add Job Category"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              variant="standard"
              required
              fullWidth
              inputProps={{ maxLength: 100 }}
              value={editing?.name ?? ""}
              onChange={(e) =>
                setEditing((s) => (s ? { ...s, name: e.target.value } : s))
              }
            />
            <TextField
              label="Description"
              variant="standard"
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              value={editing?.description ?? ""}
              onChange={(e) =>
                setEditing((s) =>
                  s ? { ...s, description: e.target.value } : s,
                )
              }
            />
            <TextField
              label="Price tier"
              variant="standard"
              select
              fullWidth
              value={editing?.price_tier_id ?? ""}
              onChange={(e) =>
                setEditing((s) =>
                  s ? { ...s, price_tier_id: e.target.value } : s,
                )
              }
              helperText={
                activeTiers.length === 0
                  ? "No active price tiers — create one in Config → Price Tiers first."
                  : "Customer-facing estimated cost is taken from the tier's base price."
              }
            >
              {activeTiers.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label} — {formatHKD(t.base_price_cents)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={{ ...greyBtn, px: 3 }}
            onClick={() => setEditing(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!editing?.name || save.isPending}
            sx={primaryBtnDialog}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={deleteOpen}
        title="Disable categories"
        body={`About to disable ${selected.length} categor${selected.length === 1 ? "y" : "ies"}. They won't appear in the customer mobile picker, but historical jobs keep their FK. Continue?`}
        confirmLabel="Disable"
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(selected)}
        onCancel={() => setDeleteOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
